import { createFileRoute, Navigate } from '@tanstack/react-router';
import { BookOpen, ExternalLink, Mail, MessageCircle } from 'lucide-react';

import {
    isIntercomAvailable,
    useIntercomSpotlight,
} from '@apps/web/components/intercom_spotlight';
import { Button } from '@apps/web/components/ui/button';
import {
    SettingsPageShell,
    useSettingsGates,
} from '@apps/web/features/settings';

export const Route = createFileRoute('/_app/settings/help-center')({
    component: HelpCenterPage,
});

const DOCUMENTATION_URL = 'https://docs.phoebe.work/docs';

// A miniature chat bubble with pulsing "typing" dots — a lightweight,
// asset-free illustration of the Intercom messenger.
function ChatBubbleAnimation() {
    return (
        <div className="bg-primary/10 flex h-11 w-11 items-center justify-center rounded-lg">
            <div className="bg-primary flex h-7 w-9 items-center justify-center gap-1 rounded-lg rounded-bl-none">
                <span className="bg-primary-foreground/90 h-1 w-1 animate-bounce rounded-full" />
                <span className="bg-primary-foreground/90 h-1 w-1 animate-bounce rounded-full [animation-delay:150ms]" />
                <span className="bg-primary-foreground/90 h-1 w-1 animate-bounce rounded-full [animation-delay:300ms]" />
            </div>
        </div>
    );
}

function HelpCenterPage() {
    const { gates } = useSettingsGates();
    const intercomAvailable = isIntercomAvailable();
    const { startSpotlight, spotlightActive, spotlight } =
        useIntercomSpotlight();

    if (!gates.canManageOrgSettings) {
        return <Navigate to="/settings" replace={true} />;
    }

    return (
        <SettingsPageShell
            title="Help Center"
            description="Find Phoebe support and documentation."
        >
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <section className="bg-card flex flex-col rounded-lg border p-5 shadow-sm">
                    <div className="bg-primary/10 flex h-11 w-11 items-center justify-center rounded-lg">
                        <BookOpen className="text-primary h-5 w-5" />
                    </div>
                    <h2 className="text-foreground mt-4 text-base font-semibold">
                        Documentation
                    </h2>
                    <p className="text-muted-foreground mt-1 text-sm">
                        Step-by-step guides for every Phoebe feature —
                        scheduling, call handling, templates, and more.
                    </p>
                    <div className="mt-4 flex-1" />
                    <Button asChild={true} className="w-fit">
                        <a
                            href={DOCUMENTATION_URL}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Browse the docs
                            <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                    </Button>
                </section>
                <section className="bg-card flex flex-col rounded-lg border p-5 shadow-sm">
                    <ChatBubbleAnimation />
                    <h2 className="text-foreground mt-4 text-base font-semibold">
                        Live chat
                    </h2>
                    <p className="text-muted-foreground mt-1 text-sm">
                        The fastest way to reach us. Click the chat bubble in
                        the bottom-left corner of any page
                        {intercomAvailable ? ' — or the button below — ' : ' '}
                        to message the Phoebe team, and we’ll reply right in the
                        app.
                    </p>
                    <div className="mt-4 flex-1" />
                    {intercomAvailable ? (
                        <Button
                            className="w-fit"
                            onClick={startSpotlight}
                            disabled={spotlightActive}
                        >
                            <MessageCircle className="mr-2 h-4 w-4" />
                            Open live chat
                        </Button>
                    ) : null}
                </section>
            </div>
            <p className="text-muted-foreground mt-6 text-sm">
                Prefer email? Reach us any time at{' '}
                <a
                    href="mailto:success@phoebe.work"
                    className="text-foreground inline-flex items-center gap-1 font-medium underline underline-offset-2 pointer-coarse:inline"
                >
                    <Mail className="h-3.5 w-3.5 pointer-coarse:mr-1 pointer-coarse:inline pointer-coarse:align-[-0.125em]" />
                    success@phoebe.work
                </a>
                .
            </p>
            {spotlight}
        </SettingsPageShell>
    );
}
